import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, viewChild } from '@angular/core';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SidebarComponent } from './sidebar/sidebar.component';
import { ChatComponent } from './chat/chat.component';
import { ChatWindowService } from './chat/chat-window.service';
import { HeaderComponent } from './header/header.component';
import { UserService } from './services/api-services/user.service';
import { filter } from 'rxjs';

/** Hosts the routed workspace and its persistent chat view. */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, HeaderComponent, SidebarComponent, ChatComponent, CdkScrollable],
})
export class AppComponent {
  /** Inject services. */
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly chatWindow = inject(ChatWindowService);
  private inactiveSince = this.document.hidden ? Date.now() : undefined;

  /** Forward to the template. */
  isUserLoggedIn = this.userService.isUserLoggedIn;
  user = this.userService.user;
  readonly isChatRoute = this.chatWindow.isChatRoute;
  readonly chatMode = this.chatWindow.mode;

  /** State. */
  private readonly mainContent = viewChild.required<ElementRef<HTMLElement>>('mainContent');

  /**
   * Constructor
   */
  constructor() {
    // Reload the whole page after 20 minutes of inactivity.
    const reloadTime = 20 * 60 * 1000;
    const onVisibilityChange = () => {
      if (this.document.hidden) {
        this.inactiveSince = Date.now();
        return;
      }

      if (this.inactiveSince !== undefined && Date.now() - this.inactiveSince >= reloadTime) {
        this.document.defaultView?.location.reload();
      }
      this.inactiveSince = undefined;
    };

    this.document.addEventListener('visibilitychange', onVisibilityChange);
    inject(DestroyRef).onDestroy(() =>
      this.document.removeEventListener('visibilitychange', onVisibilityChange)
    );

    // Scroll to the top after each navigation
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.mainContent().nativeElement.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    });
  }
}
